<template>
  <div class="w-full">
    <div v-if="hideProvider !== true" class="form-control w-full mb-4">
      <label class="label">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.provider', 'Provider') }}:</span>
      </label>
      <select v-model="selectedProviderModel" class="select select-bordered w-full">
        <option v-for="provider in voiceProviderOptions" :key="provider.id" :value="provider.id">{{ provider.label }}</option>
      </select>
    </div>
    <div class="form-control w-full mb-4">
      <label class="label">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.voice', 'Voice') }}:</span>
      </label>
      <CustomVoiceSelector
        v-model="selectedVoiceIdModel"
        :accent="accent"
        :accentEnabled="true"
        :provider="hideProvider === true ? '' : selectedProviderModel"
        :voiceType="voiceTypeModel"
        :voiceFilterMode="voiceFilterMode"
        :agentLanguage="languageModel"
        @update:accent="emit('update:accent', $event)"
      />
    </div>
    <div class="form-control w-full mb-4 rounded-box border border-base-300 bg-base-100 p-4">
      <label class="label cursor-pointer px-0 pt-0">
        <span class="label-text font-semibold" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.promptToVoice', 'Prompt to voice') }}:</span>
        <input type="checkbox" class="toggle toggle-primary" v-model="promptToVoiceEnabledModel" :disabled="selectedVoicePromptSupported !== true" />
      </label>
      <p class="mt-1 text-xs text-base-content/60" :style="labelStyle">{{ promptToVoiceStatusText }}</p>
      <div v-if="promptToVoiceEnabledModel === true" class="mt-3">
        <label class="label px-0">
          <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.voicePrompt', 'Voice prompt') }}:</span>
        </label>
        <textarea
          v-model="promptToVoicePromptModel"
          class="textarea textarea-bordered min-h-36 w-full"
          :disabled="selectedVoicePromptSupported !== true"
          :placeholder="t('aiChatAgentSetup.voiceSettings.voicePromptPlaceholder', 'Audio profile, director note, accent, pace, tone, and sample context')"
        ></textarea>
        <p class="mt-2 text-xs text-base-content/60" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.voicePromptHint', 'Used only by voices that support prompt-to-voice. The spoken answer is inserted as the transcript.') }}</p>
      </div>
    </div>
    <div class="form-control w-full mb-4">
      <label class="label">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.language', 'Language') }}:</span>
      </label>
      <CustomLanguageSelector v-model="languageModel" :options="languageOptions" :showFlagOnRight="true" />
    </div>
    <div class="form-control w-full mb-4">
      <label class="label">
        <span class="label-text" :style="labelStyle">{{ t('termsPackages.title', 'Terms Packages') }}:</span>
      </label>
      <TermsPackageSelector
        :modelValue="selectedTermsPackagesModel"
        :placeholder="t('termsPackages.selectPlaceholder', 'Select terms packages')"
        @update:modelValue="selectedTermsPackagesModel = $event"
        @update:selectedTerms="emit('update:selectedTerms', $event)"
      />
    </div>
    <div class="form-control w-full mb-4">
      <label class="label">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.speed', 'Speed') }}:</span>
      </label>
      <input type="range" min="0.5" max="2" step="0.1" v-model.number="speedModel" class="range range-primary w-full" />
      <div class="text-sm mt-1" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">{{ typeof speedModel === 'number' ? speedModel.toFixed(1) : speedModel }}x</div>
    </div>

    <div v-if="hideFillers !== true" class="voice-tool-fillers-card mb-4 rounded-box border border-base-300 bg-base-100 shadow-sm">
      <button type="button" class="btn" @click="toolFillersOpen = toolFillersOpen !== true">
        <span :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.toolFillersTitle', 'Custom filler words') }}</span>
        <span>{{ toolFillersOpen === true ? '▲' : '▼' }}</span>
      </button>
      <div v-show="toolFillersOpen === true" class="border-base-300 border-t px-4 pb-4 pt-3">
        <p class="mb-3 text-xs text-base-content/60" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.toolFillersHint', 'Short phrases the phone agent can say during normal waits and while a tool is running.') }}</p>
        <div class="form-control w-full mb-4">
          <label class="label">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.advanced.fillerLanguage', 'Language') }}</span>
          </label>
          <select v-model="selectedToolFillerLanguage" class="select select-bordered w-full">
            <option v-for="languageOption in toolFillerLanguageOptions" :key="languageOption.code" :value="languageOption.code">{{ languageOption.name }}</option>
          </select>
        </div>
        <div class="mb-5">
          <div class="mb-2 text-sm font-semibold text-base-content" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.standardFillersTitle', 'Standard filler words') }}</div>
          <div v-for="(sentence, index) in currentStandardFillerSentences" :key="`${selectedToolFillerLanguage}-standard-${index}`" class="flex items-center mb-3">
            <input
              type="text"
              class="input input-bordered flex-1 mr-3"
              :dir="toolFillerInputDirection"
              :value="sentence"
              :placeholder="t('aiChatAgentSetup.advanced.fillerSentencePlaceholder', 'Enter a filler sentence')"
              @input="updateStandardFillerSentence(index, $event.target.value)"
            />
            <button type="button" class="btn btn-error btn-sm" @click="deleteStandardFillerSentence(index)">
              {{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}
            </button>
          </div>
          <p v-if="currentStandardFillerSentences.length === 0" class="text-sm text-base-content/60 mb-3" :style="labelStyle">{{ t('aiChatAgentSetup.advanced.noFillerSentences', 'No filler sentences for this language and type.') }}</p>
          <button type="button" class="btn btn-sm" @click="addStandardFillerSentence">
            {{ t('aiChatAgentSetup.advanced.addFillerSentence', 'Add sentence') }}
          </button>
        </div>
        <div class="mb-2 text-sm font-semibold text-base-content" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.toolUseFillersTitle', 'Tool filler words') }}</div>
        <div v-for="(sentence, index) in currentToolFillerSentences" :key="`${selectedToolFillerLanguage}-toolUse-${index}`" class="flex items-center mb-3">
          <input
            type="text"
            class="input input-bordered flex-1 mr-3"
            :dir="toolFillerInputDirection"
            :value="sentence"
            :placeholder="t('aiChatAgentSetup.advanced.fillerSentencePlaceholder', 'Enter a filler sentence')"
            @input="updateToolFillerSentence(index, $event.target.value)"
          />
          <button type="button" class="btn btn-error btn-sm" @click="deleteToolFillerSentence(index)">
            {{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}
          </button>
        </div>
        <p v-if="currentToolFillerSentences.length === 0" class="text-sm text-base-content/60 mb-3" :style="labelStyle">{{ t('aiChatAgentSetup.advanced.noFillerSentences', 'No filler sentences for this language and type.') }}</p>
        <button type="button" class="btn btn-sm" @click="addToolFillerSentence">
          {{ t('aiChatAgentSetup.advanced.addFillerSentence', 'Add sentence') }}
        </button>
      </div>
    </div>

    <div :class="collapseAdvanced === true ? 'voice-advanced-card mb-2 rounded-box border border-base-300 bg-base-100 shadow-sm' : ''">
      <button
        v-if="collapseAdvanced === true"
        type="button"
        class="btn"
        @click="advancedOpen = advancedOpen !== true"
      >
        <span :style="labelStyle">{{ advancedTabLabel }}</span>
        <span>{{ advancedOpen === true ? '▲' : '▼' }}</span>
      </button>
      <div
        v-show="collapseAdvanced !== true ? true : advancedOpen === true"
        :class="collapseAdvanced === true ? 'voice-advanced-body border-base-300 border-t px-4 pb-4 pt-3' : ''"
      >
        <div v-if="hideAdvancedEngineSelectors !== true" class="voice-advanced-grid mb-2">
          <div class="form-control w-full mb-4">
            <label class="label pb-1">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.voiceAgentType', 'Voice agent type') }}:</span>
            </label>
            <CustomModeSelector v-model="voiceTypeModel" :options="voiceTypeOptions" />
          </div>
          <div class="form-control w-full mb-4">
            <label class="label pb-1">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.vadEngine', 'Turn detection') }}:</span>
            </label>
            <CustomModeSelector v-model="vadEngineModel" :options="vadEngineOptions" />
          </div>
          <div class="form-control w-full mb-4">
            <label class="label pb-1">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.audioProcessorEngine', 'Voice Focus') }}:</span>
            </label>
            <CustomModeSelector v-model="audioProcessorEngineModel" :options="resolvedAudioProcessorEngineOptions" />
          </div>
        </div>
        <div class="form-control w-full mb-4 voice-advanced-switch">
          <label class="label cursor-pointer">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.customPacing', 'Custom pacing') }}:</span>
            <input type="checkbox" class="toggle toggle-primary" v-model="customPacingEnabledModel" />
          </label>
        </div>
        <div v-if="hideModeAndCancelling !== true" class="form-control w-full mb-4 voice-advanced-switch">
          <label class="label cursor-pointer">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponse', 'Respond when both sides are idle') }}:</span>
            <input type="checkbox" class="toggle toggle-primary" v-model="idleAutoResponseEnabledModel" />
          </label>
        </div>
        <div v-if="hideModeAndCancelling !== true && idleAutoResponseEnabledModel === true" class="form-control w-full mb-4">
          <label class="label">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.startingMessageIdleAutoResponseSeconds', 'Idle after starting message in seconds') }}:</span>
          </label>
          <input type="number" min="1" max="300" step="1" v-model.number="startingMessageIdleAutoResponseSecondsModel" class="input input-bordered w-full" />
        </div>
        <div v-if="hideModeAndCancelling !== true && idleAutoResponseEnabledModel === true" class="form-control w-full mb-4">
          <label class="label">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponseSeconds', 'Idle during conversation in seconds') }}:</span>
          </label>
          <input type="number" min="1" max="300" step="1" v-model.number="idleAutoResponseSecondsModel" class="input input-bordered w-full" />
        </div>
        <div v-if="hideModeAndCancelling !== true" class="form-control w-full mb-4">
          <label class="label">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.toolUseAutoResponseSeconds', 'Forced answer after tool use in seconds (0 = off)') }}:</span>
          </label>
          <input type="number" min="0" max="300" step="1" v-model.number="toolUseAutoResponseSecondsModel" class="input input-bordered w-full" />
        </div>
        <div v-if="customPacingEnabledModel === true" class="voice-advanced-grid">
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.pacing', 'Pacing') }}:</span>
            </label>
            <input type="range" min="0.5" max="2" step="0.05" v-model.number="pacingModel" class="range range-primary w-full" />
            <div class="text-sm mt-1" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">{{ typeof pacingModel === 'number' ? pacingModel.toFixed(2) : pacingModel }}</div>
          </div>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.durationStretch', 'Duration stretch') }}:</span>
            </label>
            <input type="range" min="0.8" max="3" step="0.1" v-model.number="durationStretchModel" class="range range-primary w-full" />
            <div class="text-sm mt-1" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? { direction: 'rtl' } : { direction: 'ltr' }">{{ typeof durationStretchModel === 'number' ? durationStretchModel.toFixed(1) : durationStretchModel }}</div>
          </div>
          <div class="form-control w-full mb-4">
            <label class="label cursor-pointer">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.useDashPauses', 'Use dash pauses') }}:</span>
              <input type="checkbox" class="toggle toggle-primary" v-model="useDashPausesModel" />
            </label>
          </div>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.pauseEveryWords', 'Pause every words (0 = AI dashes)') }}:</span>
            </label>
            <input type="number" min="0" max="20" step="1" v-model.number="pauseEveryWordsModel" class="input input-bordered w-full" />
          </div>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.dashPauseMs', 'Dash pause ms') }}:</span>
            </label>
            <input type="number" min="0" max="2000" step="10" v-model.number="dashPauseMsModel" class="input input-bordered w-full" />
          </div>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.commaPauseMs', 'Comma pause ms') }}:</span>
            </label>
            <input type="number" min="0" max="2000" step="10" v-model.number="commaPauseMsModel" class="input input-bordered w-full" />
          </div>
          <div class="form-control w-full mb-4">
            <label class="label">
              <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.periodPauseMs', 'Period pause ms') }}:</span>
            </label>
            <input type="number" min="0" max="2000" step="10" v-model.number="periodPauseMsModel" class="input input-bordered w-full" />
          </div>
        </div>
        <div class="form-control w-full mb-4 voice-tone-control">
          <label class="label">
            <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.tone', 'Tone') }}:</span>
          </label>
          <CustomToneSelector v-model="toneModel" :options="toneOptions" />
        </div>
      </div>
    </div>

    <div v-if="hideModeAndCancelling !== true" class="form-control w-full">
      <label class="label pb-1">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.mode', 'Communication mode') }}:</span>
      </label>
      <CustomModeSelector v-model="modeModel" :options="modeOptions" />
    </div>
    <div v-if="hideModeAndCancelling !== true" class="form-control mt-4 w-full">
      <label class="label pb-1">
        <span class="label-text" :style="labelStyle">{{ t('aiChatAgentSetup.voiceSettings.cancellingMode', 'Cancelling mode') }}:</span>
      </label>
      <CustomModeSelector v-model="cancellingModeModel" :options="cancellingModeOptions" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from '@/i18n';
import { voiceOptions } from '../utils/voiceCatalog.js';
import CustomVoiceSelector from './CustomVoiceSelector.vue';
import CustomLanguageSelector from './CustomLanguageSelector.vue';
import CustomToneSelector from './CustomToneSelector.vue';
import CustomModeSelector from './CustomModeSelector.vue';
import TermsPackageSelector from './TermsPackageSelector.vue';
import { normalizeWaitingFillerSentences, waitingFillerDefaults } from '@/utils/waitingFillerDefaults.js';

const props = defineProps({
  voiceType: { type: String, default: 'vtv' },
  vadEngine: { type: String, default: 'tiger' },
  selectedVoiceId: { type: String, default: '' },
  accent: { type: String, default: 'Israeli' },
  language: { type: String, default: 'English' },
  languageOptions: { type: Array, default: () => [] },
  selectedTermsPackages: { type: Array, default: () => [] },
  speed: { type: Number, default: 1 },
  customPacingEnabled: { type: Boolean, default: false },
  pacing: { type: Number, default: 1.05 },
  durationStretch: { type: Number, default: 1 },
  useDashPauses: { type: Boolean, default: true },
  pauseEveryWords: { type: Number, default: 0 },
  dashPauseMs: { type: Number, default: 180 },
  commaPauseMs: { type: Number, default: 120 },
  periodPauseMs: { type: Number, default: 260 },
  idleAutoResponseEnabled: { type: Boolean, default: true },
  startingMessageIdleAutoResponseSeconds: { type: Number, default: 6 },
  idleAutoResponseSeconds: { type: Number, default: 3 },
  toolUseAutoResponseSeconds: { type: Number, default: 2 },
  promptToVoiceEnabled: { type: Boolean, default: false },
  promptToVoicePrompt: { type: String, default: '' },
  tone: { type: String, default: 'Neutral' },
  toneOptions: { type: Array, default: () => [] },
  mode: { type: String, default: 'interruptible' },
  modeOptions: { type: Array, default: () => [] },
  cancellingMode: { type: String, default: 'fast' },
  cancellingModeOptions: { type: Array, default: () => [] },
  audioProcessorEngine: { type: String, default: 'quail' },
  audioProcessorEngineOptions: { type: Array, default: () => [] },
  fillerSentences: { type: Object, default: () => ({}) },
  isRtl: { type: Boolean, default: false },
  collapseAdvanced: { type: Boolean, default: false },
  hideModeAndCancelling: { type: Boolean, default: false },
  hideProvider: { type: Boolean, default: false },
  hideFillers: { type: Boolean, default: false },
  hideAdvancedEngineSelectors: { type: Boolean, default: false },
  voiceFilterMode: { type: String, default: '' }
});

const emit = defineEmits([
  'update:voiceType',
  'update:vadEngine',
  'update:selectedVoiceId',
  'update:accent',
  'update:language',
  'update:selectedTermsPackages',
  'update:selectedTerms',
  'update:speed',
  'update:customPacingEnabled',
  'update:pacing',
  'update:durationStretch',
  'update:useDashPauses',
  'update:pauseEveryWords',
  'update:dashPauseMs',
  'update:commaPauseMs',
  'update:periodPauseMs',
  'update:idleAutoResponseEnabled',
  'update:startingMessageIdleAutoResponseSeconds',
  'update:idleAutoResponseSeconds',
  'update:toolUseAutoResponseSeconds',
  'update:promptToVoiceEnabled',
  'update:promptToVoicePrompt',
  'update:tone',
  'update:mode',
  'update:cancellingMode',
  'update:audioProcessorEngine',
  'update:fillerSentences'
]);

const { t } = useI18n();

const advancedOpen = ref(false);
const toolFillersOpen = ref(true);
const promptToVoiceEnabledLocal = ref(props.promptToVoiceEnabled === true);
const promptToVoicePromptLocal = ref(props.promptToVoicePrompt);
const selectedToolFillerLanguage = ref(normalizeLanguageCode(props.language) !== '' ? normalizeLanguageCode(props.language) : 'he');

const labelStyle = computed(() => props.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' });
const isRtl = computed(() => props.isRtl === true);

const advancedTabLabel = computed(() => t('voicePlayground.advancedTab', 'Advanced'));
const voiceTypeOptions = computed(() => [
  { id: 'vtv', label: t('aiChatAgentSetup.voiceSettings.voiceTypeVtv', 'VTV') },
  { id: 'hybrid', label: t('aiChatAgentSetup.voiceSettings.voiceTypeHybrid', 'Hybrid') },
  { id: 'layer', label: t('aiChatAgentSetup.voiceSettings.voiceTypeGenovise', 'Genovise') }
]);
const vadEngineOptions = computed(() => [
  { id: 'tiger', label: t('aiChatAgentSetup.voiceSettings.vadEngineTiger', 'Tiger') },
  { id: 'flyingBird', label: t('aiChatAgentSetup.voiceSettings.vadEngineFlyingBird', 'Flying Bird') }
]);
const resolvedAudioProcessorEngineOptions = computed(() => {
  const options = Array.isArray(props.audioProcessorEngineOptions) === true ? props.audioProcessorEngineOptions : [];
  if (options.length > 0) {
    return options;
  }
  return [
    { id: 'quail', label: t('aiChatAgentSetup.voiceSettings.voiceFocusQuailVf', 'Quail VF') },
    { id: 'internal', label: t('aiChatAgentSetup.voiceSettings.internalEngine', 'Genovise') },
    { id: 'external', label: t('aiChatAgentSetup.voiceSettings.externalEngine', 'External engine') }
  ];
});

function normalizeLanguageCode(valueParam) {
  if (valueParam === undefined || valueParam === null) {
    return '';
  }
  const raw = String(valueParam).trim().toLowerCase();
  if (raw === '') {
    return '';
  }
  if (raw === 'hebrew' || raw === 'עברית') {
    return 'he';
  }
  if (raw === 'english') {
    return 'en';
  }
  if (raw === 'spanish') {
    return 'es';
  }
  if (raw === 'italian') {
    return 'it';
  }
  if (raw === 'arabic' || raw === 'عربي' || raw === 'العربية') {
    return 'ar';
  }
  return raw.substring(0, 2);
}

function getLanguageOptionCode(valueParam) {
  const value = valueParam !== null && valueParam !== undefined && typeof valueParam === 'object' ? valueParam : {};
  return normalizeLanguageCode(value.code !== undefined ? value.code : (value.id !== undefined ? value.id : (value.value !== undefined ? value.value : valueParam)));
}

function getLanguageOptionName(valueParam, codeParam) {
  const value = valueParam !== null && valueParam !== undefined && typeof valueParam === 'object' ? valueParam : {};
  const label = value.name !== undefined ? value.name : (value.label !== undefined ? value.label : value.text);
  if (typeof label === 'string' && label.trim() !== '') {
    return label.trim();
  }
  const code = typeof codeParam === 'string' && codeParam !== '' ? codeParam : getLanguageOptionCode(valueParam);
  return code !== '' ? code.toUpperCase() : '';
}

const toolFillerLanguageOptions = computed(() => {
  const output = [];
  const seen = new Set();
  const configuredLanguages = Array.isArray(props.languageOptions) === true ? props.languageOptions : [];
  for (let index = 0; index < configuredLanguages.length; index = index + 1) {
    const code = getLanguageOptionCode(configuredLanguages[index]);
    if (code === '' || seen.has(code) === true) {
      continue;
    }
    seen.add(code);
    output.push({ code: code, name: getLanguageOptionName(configuredLanguages[index], code) });
  }
  const selectedLanguageCode = normalizeLanguageCode(props.language);
  if (selectedLanguageCode !== '' && seen.has(selectedLanguageCode) !== true) {
    seen.add(selectedLanguageCode);
    output.push({ code: selectedLanguageCode, name: selectedLanguageCode.toUpperCase() });
  }
  const defaultLanguages = Object.keys(waitingFillerDefaults);
  for (let index = 0; index < defaultLanguages.length; index = index + 1) {
    const code = defaultLanguages[index];
    if (seen.has(code) === true) {
      continue;
    }
    seen.add(code);
    output.push({ code: code, name: code.toUpperCase() });
  }
  return output;
});
const currentToolFillerSentences = computed(() => {
  const normalized = normalizeWaitingFillerSentences(props.fillerSentences);
  const language = normalized[selectedToolFillerLanguage.value];
  if (language === undefined || language === null || typeof language !== 'object') {
    return [];
  }
  const sentences = language.toolUse;
  return Array.isArray(sentences) === true ? sentences : [];
});
const currentStandardFillerSentences = computed(() => {
  const normalized = normalizeWaitingFillerSentences(props.fillerSentences);
  const language = normalized[selectedToolFillerLanguage.value];
  if (language === undefined || language === null || typeof language !== 'object') {
    return [];
  }
  const sentences = language.standard;
  return Array.isArray(sentences) === true ? sentences : [];
});
const toolFillerInputDirection = computed(() => selectedToolFillerLanguage.value === 'he' || selectedToolFillerLanguage.value === 'ar' ? 'rtl' : 'ltr');

function emitToolFillerSentences(valueParam) {
  emit('update:fillerSentences', normalizeWaitingFillerSentences(valueParam));
}

function ensureToolFillerLanguage(valueParam, languageParam) {
  const normalized = normalizeWaitingFillerSentences(valueParam);
  const language = typeof languageParam === 'string' && languageParam !== '' ? languageParam : 'he';
  if (normalized[language] === undefined) {
    normalized[language] = { standard: [], toolUse: [] };
  }
  if (Array.isArray(normalized[language].toolUse) !== true) {
    normalized[language].toolUse = [];
  }
  if (Array.isArray(normalized[language].standard) !== true) {
    normalized[language].standard = [];
  }
  return normalized;
}

function updateToolFillerSentence(indexParam, valueParam) {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].toolUse.slice();
  if (indexParam < 0 || indexParam >= sentences.length) {
    return;
  }
  sentences[indexParam] = typeof valueParam === 'string' ? valueParam : '';
  normalized[language].toolUse = sentences;
  emitToolFillerSentences(normalized);
}

function updateStandardFillerSentence(indexParam, valueParam) {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].standard.slice();
  if (indexParam < 0 || indexParam >= sentences.length) {
    return;
  }
  sentences[indexParam] = typeof valueParam === 'string' ? valueParam : '';
  normalized[language].standard = sentences;
  emitToolFillerSentences(normalized);
}

function deleteToolFillerSentence(indexParam) {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].toolUse.slice();
  if (indexParam < 0 || indexParam >= sentences.length) {
    return;
  }
  sentences.splice(indexParam, 1);
  normalized[language].toolUse = sentences;
  emitToolFillerSentences(normalized);
}

function deleteStandardFillerSentence(indexParam) {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].standard.slice();
  if (indexParam < 0 || indexParam >= sentences.length) {
    return;
  }
  sentences.splice(indexParam, 1);
  normalized[language].standard = sentences;
  emitToolFillerSentences(normalized);
}

function addToolFillerSentence() {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].toolUse.slice();
  sentences.push('');
  normalized[language].toolUse = sentences;
  emitToolFillerSentences(normalized);
}

function addStandardFillerSentence() {
  const language = selectedToolFillerLanguage.value;
  const normalized = ensureToolFillerLanguage(props.fillerSentences, language);
  const sentences = normalized[language].standard.slice();
  sentences.push('');
  normalized[language].standard = sentences;
  emitToolFillerSentences(normalized);
}

function normalizeVoiceType(value) {
  if (typeof value !== 'string') {
    return 'vtv';
  }
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'hybrid') {
    return 'hybrid';
  }
  if (normalized === 'vtvwithtts') {
    return 'hybrid';
  }
  if (normalized === 'realtimewithtts') {
    return 'hybrid';
  }
  if (normalized === 'genovise') {
    return 'layer';
  }
  if (normalized === 'layer') {
    return 'layer';
  }
  if (normalized === 'layers') {
    return 'layer';
  }
  if (normalized === 'vllm') {
    return 'layer';
  }
  if (normalized === 'sttllmtts') {
    return 'layer';
  }
  if (normalized === 'realtimewithcustomsttopenai') {
    return 'layer';
  }
  if (normalized === 'openairealtimecustomstt') {
    return 'layer';
  }
  if (normalized === 'realtimewithcustomsttdefault') {
    return 'layer';
  }
  if (normalized === 'geminilive' || normalized === 'geminirealtime' || normalized === 'lite') {
    return 'geminiLive';
  }
  return 'vtv';
}

const voiceTypeModel = computed({
  get: () => normalizeVoiceType(props.voiceType),
  set: (value) => emit('update:voiceType', normalizeVoiceType(value))
});
function normalizeVadEngine(valueParam) {
  const normalized = typeof valueParam === 'string' ? valueParam.trim().toLowerCase().replace(/[^a-z0-9]+/g, '') : '';
  if (normalized === 'flyingbird') {
    return 'flyingBird';
  }
  return 'tiger';
}
const vadEngineModel = computed({
  get: () => normalizeVadEngine(props.vadEngine),
  set: (value) => emit('update:vadEngine', normalizeVadEngine(value))
});
const selectedVoiceIdModel = computed({
  get: () => props.selectedVoiceId,
  set: (value) => {
    const voiceId = typeof value === 'string' ? value : '';
    emit('update:selectedVoiceId', voiceId);
    if (isDefaultRealtimeVoiceOption(findVoiceOptionById(voiceId)) === true) {
      emit('update:voiceType', 'vtv');
    }
  }
});
const selectedVoiceOption = computed(() => {
  const voiceId = typeof props.selectedVoiceId === 'string' ? props.selectedVoiceId.trim() : '';
  if (voiceId === '') {
    return null;
  }
  const found = findVoiceOptionById(voiceId);
  if (found !== undefined && found !== null) {
    return found;
  }
  return null;
});
function findVoiceOptionById(voiceIdParam) {
  const voiceId = typeof voiceIdParam === 'string' ? voiceIdParam.trim() : '';
  if (voiceId === '') {
    return null;
  }
  for (let index = 0; index < voiceOptions.length; index = index + 1) {
    const entry = voiceOptions[index];
    if (entry !== undefined && entry !== null && entry.voiceId === voiceId) {
      return entry;
    }
  }
  return null;
}
function getVoiceProvider(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  const provider = typeof voice.providerId === 'string' ? voice.providerId : '';
  return typeof provider === 'string' ? provider.trim().toLowerCase() : '';
}
function getVoiceRuntimeType(voiceParam) {
  const voice = voiceParam !== null && voiceParam !== undefined && typeof voiceParam === 'object' ? voiceParam : {};
  return typeof voice.runtimeType === 'string' ? voice.runtimeType.trim().toLowerCase() : '';
}
function isDefaultRealtimeVoiceOption(voiceParam) {
  if (getVoiceRuntimeType(voiceParam) === 'default') { return true; }
  return false;
}
function normalizeVoiceFilterMode(valueParam) {
  const normalized = typeof valueParam === 'string' ? valueParam.trim().toLowerCase().replace(/[^a-z0-9]+/g, '') : '';
  if (normalized === 'default') {
    return 'default';
  }
  if (normalized === 'quality') {
    return 'quality';
  }
  return '';
}
function voiceMatchesFilterMode(voiceParam) {
  const filterMode = normalizeVoiceFilterMode(props.voiceFilterMode);
  if (filterMode === 'default') {
    return isDefaultRealtimeVoiceOption(voiceParam);
  }
  if (filterMode === 'quality') {
    return isDefaultRealtimeVoiceOption(voiceParam) !== true;
  }
  return true;
}
function formatVoiceProvider(valueParam) {
  const value = typeof valueParam === 'string' ? valueParam.trim() : '';
  if (value === '') {
    return '';
  }
  return value.substring(0, 1).toUpperCase() + value.substring(1);
}
const voiceProviderOptions = computed(() => {
  const providers = [];
  const seen = new Set();
  for (let index = 0; index < voiceOptions.length; index = index + 1) {
    const voice = voiceOptions[index];
    if (voiceMatchesFilterMode(voice) !== true) {
      continue;
    }
    const provider = getVoiceProvider(voice);
    if (provider === '' || seen.has(provider) === true) {
      continue;
    }
    seen.add(provider);
    providers.push({ id: provider, label: typeof voice.providerLabel === 'string' && voice.providerLabel.trim() !== '' ? voice.providerLabel.trim() : formatVoiceProvider(provider) });
  }
  return providers;
});
const selectedProviderModel = computed({
  get: () => {
    const selectedProvider = getVoiceProvider(selectedVoiceOption.value);
    const providerOptions = voiceProviderOptions.value;
    if (selectedProvider !== '') {
      for (let index = 0; index < providerOptions.length; index = index + 1) {
        if (providerOptions[index].id === selectedProvider) {
          return selectedProvider;
        }
      }
    }
    return providerOptions.length > 0 ? providerOptions[0].id : '';
  },
  set: (value) => {
    const provider = typeof value === 'string' ? value.trim().toLowerCase() : '';
    for (let index = 0; index < voiceOptions.length; index = index + 1) {
      const voice = voiceOptions[index];
      if (voiceMatchesFilterMode(voice) === true && getVoiceProvider(voice) === provider && typeof voice.voiceId === 'string' && voice.voiceId !== '') {
        emit('update:selectedVoiceId', voice.voiceId);
        return;
      }
    }
  }
});
const selectedVoicePromptSupported = computed(() => {
  const voice = selectedVoiceOption.value;
  if (voice === null) {
    return false;
  }
  if (voice.promptToVoiceSupported === true) {
    return true;
  }
  const features = Array.isArray(voice.supportFeatures) === true ? voice.supportFeatures : [];
  for (let index = 0; index < features.length; index += 1) {
    if (String(features[index]).trim() === 'promptToVoice') {
      return true;
    }
  }
  const voiceId = typeof voice.voiceId === 'string' ? voice.voiceId.trim().toLowerCase() : '';
  return voiceId.indexOf('gstudio') === 0;
});
const promptToVoiceStatusText = computed(() => {
  if (selectedVoicePromptSupported.value === true) {
    return t('aiChatAgentSetup.voiceSettings.promptToVoiceSupportedHint', 'Available for this voice. Enable it to guide the voice engine with an audio profile and director note.');
  }
  return t('aiChatAgentSetup.voiceSettings.promptToVoiceUnsupportedHint', 'Select a compatible live voice to enable prompt-to-voice.');
});
const promptToVoiceEnabledModel = computed({
  get: () => selectedVoicePromptSupported.value === true && promptToVoiceEnabledLocal.value === true,
  set: (value) => {
    const nextValue = selectedVoicePromptSupported.value === true && value === true;
    promptToVoiceEnabledLocal.value = nextValue;
    emit('update:promptToVoiceEnabled', nextValue);
  }
});
const promptToVoicePromptModel = computed({
  get: () => promptToVoicePromptLocal.value,
  set: (value) => {
    promptToVoicePromptLocal.value = typeof value === 'string' ? value : '';
    emit('update:promptToVoicePrompt', typeof value === 'string' ? value : '');
  }
});
watch(() => props.promptToVoiceEnabled, (value) => {
  promptToVoiceEnabledLocal.value = value === true;
});
watch(() => props.promptToVoicePrompt, (value) => {
  promptToVoicePromptLocal.value = typeof value === 'string' ? value : '';
});
watch(() => props.language, (value) => {
  const nextLanguage = normalizeLanguageCode(value);
  if (nextLanguage !== '') {
    selectedToolFillerLanguage.value = nextLanguage;
  }
});
watch(selectedVoicePromptSupported, (value) => {
  if (value !== true && promptToVoiceEnabledLocal.value === true) {
    promptToVoiceEnabledLocal.value = false;
    emit('update:promptToVoiceEnabled', false);
  }
});
const languageModel = computed({
  get: () => props.language,
  set: (value) => emit('update:language', value)
});
const selectedTermsPackagesModel = computed({
  get: () => props.selectedTermsPackages,
  set: (value) => emit('update:selectedTermsPackages', Array.isArray(value) === true ? value : [])
});
const speedModel = computed({
  get: () => props.speed,
  set: (value) => emit('update:speed', value)
});
const customPacingEnabledModel = computed({
  get: () => props.customPacingEnabled,
  set: (value) => emit('update:customPacingEnabled', value === true)
});
const pacingModel = computed({
  get: () => props.pacing,
  set: (value) => emit('update:pacing', value)
});
const durationStretchModel = computed({
  get: () => props.durationStretch,
  set: (value) => emit('update:durationStretch', value)
});
const useDashPausesModel = computed({
  get: () => props.useDashPauses,
  set: (value) => emit('update:useDashPauses', value === true)
});
const pauseEveryWordsModel = computed({
  get: () => props.pauseEveryWords,
  set: (value) => emit('update:pauseEveryWords', value)
});
const dashPauseMsModel = computed({
  get: () => props.dashPauseMs,
  set: (value) => emit('update:dashPauseMs', value)
});
const commaPauseMsModel = computed({
  get: () => props.commaPauseMs,
  set: (value) => emit('update:commaPauseMs', value)
});
const periodPauseMsModel = computed({
  get: () => props.periodPauseMs,
  set: (value) => emit('update:periodPauseMs', value)
});
const idleAutoResponseEnabledModel = computed({
  get: () => props.idleAutoResponseEnabled === true,
  set: (value) => emit('update:idleAutoResponseEnabled', value === true)
});
const startingMessageIdleAutoResponseSecondsModel = computed({
  get: () => props.startingMessageIdleAutoResponseSeconds,
  set: (value) => {
    const seconds = Number(value);
    emit('update:startingMessageIdleAutoResponseSeconds', Number.isFinite(seconds) === true && seconds >= 1 ? Math.min(300, seconds) : 6);
  }
});
const idleAutoResponseSecondsModel = computed({
  get: () => props.idleAutoResponseSeconds,
  set: (value) => {
    const seconds = Number(value);
    emit('update:idleAutoResponseSeconds', Number.isFinite(seconds) === true && seconds >= 1 ? Math.min(300, seconds) : 3);
  }
});
const toolUseAutoResponseSecondsModel = computed({
  get: () => props.toolUseAutoResponseSeconds,
  set: (value) => {
    const seconds = Number(value);
    emit('update:toolUseAutoResponseSeconds', Number.isFinite(seconds) === true && seconds >= 0 ? Math.min(300, seconds) : 2);
  }
});
const toneModel = computed({
  get: () => props.tone,
  set: (value) => emit('update:tone', value)
});
const modeModel = computed({
  get: () => props.mode,
  set: (value) => emit('update:mode', value)
});
const cancellingModeModel = computed({
  get: () => props.cancellingMode,
  set: (value) => emit('update:cancellingMode', value)
});
const audioProcessorEngineModel = computed({
  get: () => props.audioProcessorEngine,
  set: (value) => emit('update:audioProcessorEngine', value)
});
</script>

<style scoped>
.voice-advanced-card {
  overflow: hidden;
  background:
    linear-gradient(135deg, hsl(var(--b1)) 0%, hsl(var(--b2)) 100%);
}

.voice-tool-fillers-card {
  overflow: hidden;
  background:
    linear-gradient(135deg, hsl(var(--b1)) 0%, hsl(var(--b2)) 100%);
}

.voice-tool-fillers-card > .btn {
  width: 100%;
  min-height: 3rem;
  justify-content: space-between;
  border-width: 0;
  border-radius: 0;
  background: transparent;
  font-weight: 700;
  text-transform: none;
}

.voice-tool-fillers-card > .btn:hover {
  background: hsl(var(--b2));
}

.voice-advanced-card > .btn {
  width: 100%;
  min-height: 3rem;
  justify-content: space-between;
  border-width: 0;
  border-radius: 0;
  background: transparent;
  font-weight: 700;
  text-transform: none;
}

.voice-advanced-card > .btn:hover {
  background: hsl(var(--b2));
}

.voice-advanced-body {
  background: hsl(var(--b1) / 0.72);
}

.voice-advanced-switch {
  border-radius: 0.85rem;
  border: 1px solid hsl(var(--bc) / 0.08);
  background: hsl(var(--b2) / 0.72);
  padding: 0.25rem 0.75rem;
}

.voice-advanced-grid {
  display: flex;
  flex-wrap: wrap;
  margin-left: -0.45rem;
  margin-right: -0.45rem;
}

.voice-advanced-grid .form-control {
  box-sizing: border-box;
  flex: 0 0 50%;
  max-width: 50%;
  padding: 0.65rem 0.45rem;
}

.voice-advanced-grid .form-control > label,
.voice-tone-control > label {
  min-height: 2rem;
}

.voice-advanced-grid .input,
.voice-advanced-grid .range,
.voice-tone-control :deep(.select),
.voice-tone-control :deep(.input) {
  width: 100%;
}

.voice-tone-control {
  margin-bottom: 0;
  border-radius: 0.85rem;
  border: 1px solid hsl(var(--bc) / 0.08);
  background: hsl(var(--b2) / 0.5);
  padding: 0.65rem 0.75rem 0.8rem;
}

@media (max-width: 640px) {
  .voice-advanced-grid .form-control {
    flex-basis: 100%;
    max-width: 100%;
  }
}
</style>
